import config from "../config";
import { fetchAuthSession } from "aws-amplify/auth";
import axios, { AxiosError, AxiosInstance, AxiosPromise, AxiosResponse, InternalAxiosRequestConfig } from "axios";

export interface IdValueQuery {
  id: string;
  value: string;
}

const ERROR_MESSAGE_MAPPING: { [key: string]: string } = {
  "Unique index violation": "A role with this name already exists, please choose a unique name.",
};

export interface AjaxRequestConfig extends InternalAxiosRequestConfig {
  skip_bearer_injection?: boolean;
  skip_token_refresh?: boolean;
  serialize?: boolean;
}

export interface AxiosHubError extends AxiosError {
  config: AjaxRequestConfig;
}

export interface AjaxInstance extends AxiosInstance {
  (config: Partial<AjaxRequestConfig>): AxiosPromise;
}

export interface AjaxResponse extends AxiosResponse {}

const ajax: AjaxInstance = axios.create();

const init = () => {
  ajax.defaults.baseURL = config?.configEndpoints?.baseEndpoint;
  ajax.defaults.headers.post["Content-Type"] = "application/json";
  ajax.defaults.headers.put["Content-Type"] = "application/json";
  ajax.interceptors.request.use(requestIntercept, (error) => Promise.reject(error));
  ajax.interceptors.response.use((success) => success, responseFailIntercept);
  return ajax;
};

const requestIntercept = async (request: AjaxRequestConfig) => {
  if (!request?.skip_bearer_injection) {
    const session = await fetchAuthSession();
    if (session?.tokens?.idToken) {
      request.headers.Authorization = `Bearer ${session.tokens.idToken.toString()}`;
    }
  }

  if (request?.serialize) {
    request.paramsSerializer = function (params) {
      const parsed = new URLSearchParams();
      for (let key of Object.keys(params)) {
        if (key === "queries") {
          params?.queries?.forEach((q: IdValueQuery) => parsed.append(q?.id, q?.value));
        } else if (params?.[key] !== undefined) {
          const isArray = Array.isArray(params?.[key]);
          parsed.append(key, isArray ? params?.[key]?.toString() : params?.[key]);
        }
      }
      return parsed.toString();
    };
  }
  return request;
};

const responseFailIntercept = async (error: AxiosHubError) => {
  const response: AxiosResponse | undefined = error?.response;

  if (response?.data?.message) {
    response.data.message = mapErrorMessage(response.data.message);
  }

  return Promise.reject(response);
};

let isRenewing = false;
let failedQueue: any[] = [];

const processQueue = () => {
  failedQueue.forEach((promise: any) => {
    if (promise?.request) {
      axios(promise?.request)
        .then((resp) => promise?.resolve(resp))
        .catch((err) => promise?.reject(err));
    }
  });
  failedQueue = [];
};

export const mapErrorMessage = (error: string): string => {
  return ERROR_MESSAGE_MAPPING[error] || error;
};

export const requiresAccountId = (accountId: string | undefined) => {
  if (!accountId) {
    throw new Error("Account ID must be defined");
  }
};

export default init();
